import { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  getIsLoadingWorkingProject,
  getWorkingProjectSessions,
  getWorkingProjectUuid,
  selectDuplicatedProject,
  selectDuplicateSession,
  selectIsDuplicatingSession,
  selectIsProjectDuplicating,
  selectProjectIsAutoGeneratedName,
  selectProjectIsTest,
  selectProjectName,
  selectSingleSessionUuid,
  selectWorkingProjectDefaultLanguage,
  selectWorkingProjectIsTemplate,
  selectWorkingProjectType,
} from '@/store/slices/project/selectors';
import GlobalTemplatesChannelGuard from '@ui/auth-wrapper/global-templates-channel-guard';
import EditGlobalTemplateTypeModal from './edit-global-template-type-modal';
import { ICONS } from '@ui/icons/types';
import OptionalComponent from '@ui/optional-component/optional-component';
import Popover from '@ui/popover/popover';
import Text from '@ui/text/text/text';
import DeleteProjectModal from './delete-project-modal';
import { EventType } from '@/types/projects.types';
import DeleteSessionModal from './delete-session-modal';
import SaveAsTemplateModal from './save-template-modal/save-as-template-modal';
import StreamingCredentialsModal from './streaming-credentials-modal';
import { EBroadcastTypes, SessionTypesEnum } from '@/types/session.types';
import Button from '@ui/buttons/button/button';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import DuplicateProjectOrSessionModal from '../duplicate-session-or-project-modal/duplicate-session-or-project-modal';
import AuthWrapper from '@ui/auth-wrapper/auth-wrapper';
import { EPermissions } from '@/types/permissions.types';
import Icon from '@ui/icons/icon';
import AddGroupModal from './add-to-folder-modal';
import CreatePreviewProject from './create-preview-project';
import Container from '@ui/container/container';
import ModerateButton from './controls/buttons/moderate-button';
import DuplicateProjectButton from './controls/buttons/duplicate-project-button';
import { TButtonAndLinkDesigns } from '@styles/design-system/local-components/local-components.types';

const ProjectOverviewPopoverActions = () => {
  const projectUuid = useAppSelector(getWorkingProjectUuid);
  const projectName = useAppSelector(selectProjectName);
  const loadingWorkingProject = useAppSelector(getIsLoadingWorkingProject);
  const isTestProject = useAppSelector(selectProjectIsTest);
  const projectType = useAppSelector(selectWorkingProjectType);
  const isTemplate = useAppSelector(selectWorkingProjectIsTemplate);
  const singleSessionUuid = useAppSelector(selectSingleSessionUuid);
  const sessions = useAppSelector(getWorkingProjectSessions);
  const isAutoGeneratedName = useAppSelector(selectProjectIsAutoGeneratedName);
  const defaultLanguage = useAppSelector(selectWorkingProjectDefaultLanguage);
  const isDuplicatingSession = useAppSelector(selectIsDuplicatingSession);
  const isDuplicatingProject = useAppSelector(selectIsProjectDuplicating);
  const duplicatedSession = useAppSelector(selectDuplicateSession);
  const duplicatedProject = useAppSelector(selectDuplicatedProject);

  const [showDeleteProjectModal, setShowDeleteProjectModal] = useState(false);
  const [showDeleteSessionModal, setShowDeleteSessionModal] = useState(false);
  const [showSaveAsTemplateModal, setShowSaveAsTemplateModal] = useState(false);
  const [showStreamingCredentilsModal, setShowStreamingCredentilsModal] = useState(false);
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState<'project' | 'session' | null>(null);
  const [showEditGlobalTemplateTypeModal, setShowEditGlobalTemplateTypeModal] = useState(false);

  const dispatch = useAppDispatch();

  const params = useParams();
  const popoverPortalRef = useRef<HTMLDivElement | null>(null);

  const language = params.language || defaultLanguage || 'en';

  const isEventProject = projectType === EventType.Event;

  const isSingleSession = !!singleSessionUuid;
  const isMultiSession = (sessions || []).length > 1;

  const sessionUuid = useMemo(() => {
    if (params?.sessionUuid) return params.sessionUuid;
    if (singleSessionUuid) {
      return singleSessionUuid;
    }
  }, [params?.sessionUuid, singleSessionUuid]);

  const session = useMemo(() => {
    return (sessions || []).find((s) => s.uuid === sessionUuid);
  }, [sessionUuid, sessions]);

  const updateProjectTestState = useCallback(() => {
    dispatch(saveWorkingProjectByFields({ is_test_event: !isTestProject }));
  }, [dispatch, isTestProject]);

  const allowRtmp = useMemo(() => {
    if (!session?.broadcast_type) {
      return false;
    }

    if (session?.session_type !== SessionTypesEnum.broadcast) {
      return false;
    }

    if (![EBroadcastTypes.rtmps, EBroadcastTypes.greenroom].includes(session.broadcast_type)) {
      return false;
    }

    return true;
  }, [session]);

  const showDivider = useMemo(() => {
    return (
      (isEventProject && !!sessionUuid) ||
      (!!sessionUuid && !isSingleSession) ||
      (isSingleSession && !isEventProject) ||
      !sessionUuid
    );
  }, [isEventProject, isSingleSession, sessionUuid]);

  if (!projectUuid) return null;
  return (
    <div data-component="ProjectOverviewPopoverActions">
      <Popover
        ignoreClasses={['.keep-open']}
        closeOnClick
        width="180px"
        disabled={loadingWorkingProject}
        containerClassName="radius-12"
        cycleWithArrowKeys
        portalEl={popoverPortalRef}
        renderTriggerButton={({ open }) => (
          <Button
            className="flex fd-row jc-center ai-center radius-full padding-0 margin-l-16"
            width="32px"
            height="32px"
            disabled={loadingWorkingProject}
            design={TButtonAndLinkDesigns.LocalBttnSecTransp}
            fixedState={open ? 'hover' : undefined}
          >
            <Icon name={ICONS.MENU_VERTICAL2} size={12} />
          </Button>
        )}
      >
        {({ triggerBounds }) => {
          return (
            <Container
              background={'var(--gray-800)'}
              className="position-fixed z-20 overflow-y-auto"
              borderRadius="12px"
              maxHeight="calc(100vh - var(--header-height-lhmp, 0px))"
              style={{
                top: triggerBounds?.top || 0,
                left: (triggerBounds?.left || 0) + (triggerBounds?.width || 0),
                transform: 'translate(-100%, 40px)',
                minWidth: 'max-content',
              }}
            >
              <OptionalComponent display={!isEventProject && !isTemplate}>
                <AuthWrapper allowedRoles={[EPermissions.GroupAdmin]}>
                  <Button
                    hoverBackgroundColor="var(--gray-700)"
                    outlineOnFocus={false}
                    className="radius-0 text-left padding-v-10 padding-h-16 width-100-percent flex ai-center gap-10"
                    onClick={() => setShowSaveAsTemplateModal(true)}
                  >
                    <Icon name={ICONS.BOOKMARK_ADD} />
                    <Text color="var(--gray-100)" className="P2">
                      Save as template
                    </Text>
                  </Button>
                </AuthWrapper>
              </OptionalComponent>
              <OptionalComponent display={!!isTemplate}>
                <GlobalTemplatesChannelGuard>
                  <Button
                    hoverBackgroundColor="var(--gray-700)"
                    outlineOnFocus={false}
                    className="radius-0 text-left padding-v-10 padding-h-16 width-100-percent flex ai-center gap-10"
                    onClick={() => setShowEditGlobalTemplateTypeModal(true)}
                  >
                    <Icon name={ICONS.BOOKMARK_ADD} />
                    <Text color="var(--gray-100)" className="P2">
                      Edit Global Template Type
                    </Text>
                  </Button>
                </GlobalTemplatesChannelGuard>
              </OptionalComponent>
              <Button
                hoverBackgroundColor="var(--gray-700)"
                outlineOnFocus={false}
                className="radius-0 text-left padding-v-10 padding-h-16 width-100-percent flex ai-center gap-10"
                onClick={() => setShowAddGroupModal(true)}
              >
                <Icon name={ICONS.FOLDER_PLUS} />
                <Text color="var(--gray-100)" className="P2">
                  Add to folder
                </Text>
              </Button>
              <hr style={{ height: '1px', backgroundColor: 'var(--gray-700)', border: 'none', margin: 0 }} />
              <DuplicateProjectButton
                mode={params.sessionUuid ? 'session' : 'project'}
                setShowDuplicateModal={setShowDuplicateModal}
              />

              {/*
                Sandbox shown everywhere except:
                  - events -> session view
              */}
              <OptionalComponent display={(isSingleSession || (isMultiSession && !sessionUuid)) && !isTemplate}>
                <Button
                  hoverBackgroundColor="var(--gray-700)"
                  outlineOnFocus={false}
                  className="radius-0 text-left padding-v-10 padding-h-16 width-100-percent flex ai-center gap-10 keep-open"
                  onClick={updateProjectTestState}
                >
                  <Icon name={ICONS.BUCKET} />
                  <Text color="var(--gray-100)" className="P2">
                    Sandbox
                  </Text>
                </Button>
              </OptionalComponent>
              <OptionalComponent display={!isTemplate}>
                <hr style={{ height: '1px', backgroundColor: 'var(--gray-700)', border: 'none', margin: 0 }} />
                <ModerateButton session={session} />

                <hr style={{ height: '1px', backgroundColor: 'var(--gray-700)', border: 'none', margin: 0 }} />
                <OptionalComponent display={allowRtmp}>
                  <Button
                    hoverBackgroundColor="var(--gray-700)"
                    outlineOnFocus={false}
                    className="radius-0 text-left padding-v-10 padding-h-16 width-100-percent flex ai-center gap-10"
                    onClick={() => setShowStreamingCredentilsModal(true)}
                  >
                    <Icon name={ICONS.HOME_WIFI} />
                    <Text color="var(--gray-100)" className="P2">
                      Streaming info
                    </Text>
                  </Button>
                </OptionalComponent>

                <AuthWrapper allowedRoles={[EPermissions.GroupAdmin]}>
                  <Button
                    className="radius-0 text-left padding-v-10 padding-h-16 width-100-percent flex ai-center gap-10"
                    hoverBackgroundColor="var(--gray-700)"
                    onClick={() => setShowPreviewModal(true)}
                    disabled={isAutoGeneratedName}
                  >
                    <Icon name={ICONS.KEY}></Icon>
                    <Text color="var(--gray-100)" className="P2">
                      Private link
                    </Text>
                  </Button>
                </AuthWrapper>
              </OptionalComponent>

              <OptionalComponent display={showDivider}>
                <hr style={{ height: '1px', backgroundColor: 'var(--gray-700)', border: 'none', margin: 0 }} />
              </OptionalComponent>

              <OptionalComponent display={!!sessionUuid && !isSingleSession}>
                <Button
                  hoverBackgroundColor="var(--gray-700)"
                  outlineOnFocus={false}
                  className="width-100-percent radius-0 padding-0"
                  onClick={() => setShowDeleteSessionModal(true)}
                >
                  <Text
                    color="var(--semantic-red-500)"
                    hoverColor="var(--semantic-red-100)"
                    width="100%"
                    height="100%"
                    className="P2 flex ai-center text-left gap-10 padding-v-10 padding-h-16"
                    inheritButtonHover
                  >
                    <div className="flex">
                      <Icon name={ICONS.DELETE_TRASH_BIN} />
                    </div>
                    <span>Delete session</span>
                  </Text>
                </Button>
              </OptionalComponent>

              <OptionalComponent display={isSingleSession || !sessionUuid}>
                <AuthWrapper allowedRoles={[EPermissions.GroupAdmin]}>
                  <Button
                    hoverBackgroundColor="var(--gray-700)"
                    outlineOnFocus={false}
                    className="width-100-percent radius-0 padding-0"
                    onClick={() => setShowDeleteProjectModal(true)}
                  >
                    <Text
                      color="var(--semantic-red-500)"
                      hoverColor="var(--semantic-red-100)"
                      width="100%"
                      height="100%"
                      className="P2 flex ai-center text-left gap-10 padding-v-10 padding-h-16"
                      inheritButtonHover
                    >
                      <div className="flex">
                        <Icon name={ICONS.DELETE_TRASH_BIN} />
                      </div>
                      <span>Delete project</span>
                    </Text>
                  </Button>
                </AuthWrapper>
              </OptionalComponent>
              {/* keeping around just in case */}
              {/* <OptionalComponent display={!isEventProject}>
          <UpdateProjectType
            newType={EventType.Event}
            setShowModal={(newType) => dispatch(setShowUpdateProjectTypeModal(newType))}
          />
        </OptionalComponent> */}
              {/*
          Moderate only shown when we have a session:
            - events project -> session view
            - townhall/webinar project -> project view
        */}
              {/* <AuthWrapper allowedRoles={[EPermissions.GroupAdmin]}>
          <ModerationButton />
        </AuthWrapper> */}
            </Container>
          );
        }}
      </Popover>

      {/* because we are removing/discouraging "event" types, we don't allow creating templates from event types */}
      <OptionalComponent display={!isEventProject}>
        <SaveAsTemplateModal open={showSaveAsTemplateModal} handleClose={() => setShowSaveAsTemplateModal(false)} />
      </OptionalComponent>

      <AddGroupModal open={showAddGroupModal} handleClose={() => setShowAddGroupModal(false)} />

      <OptionalComponent display={allowRtmp}>
        <StreamingCredentialsModal
          open={showStreamingCredentilsModal}
          handleClose={() => setShowStreamingCredentilsModal(false)}
          session={session}
          language={language}
        />
      </OptionalComponent>

      <OptionalComponent display={isSingleSession || !sessionUuid}>
        <DeleteProjectModal
          projectUuid={projectUuid}
          projectName={projectName || ''}
          showDeleteModal={showDeleteProjectModal}
          setShowDeleteModal={setShowDeleteProjectModal}
        />
      </OptionalComponent>

      <OptionalComponent display={!!sessionUuid && !isSingleSession}>
        <DeleteSessionModal
          projectUuid={projectUuid}
          sessionId={session?.session}
          showDeleteModal={showDeleteSessionModal}
          setShowDeleteModal={setShowDeleteSessionModal}
        />
      </OptionalComponent>

      <DuplicateProjectOrSessionModal
        isOpen={!!showDuplicateModal}
        onClose={() => setShowDuplicateModal(null)}
        duplicatedSession={duplicatedSession}
        duplicatedProject={duplicatedProject}
        isDuplicating={isDuplicatingSession || isDuplicatingProject}
        mode={showDuplicateModal}
      />

      <CreatePreviewProject open={showPreviewModal} handleClose={() => setShowPreviewModal(false)} />

      <OptionalComponent display={!!isTemplate}>
        <GlobalTemplatesChannelGuard>
          <EditGlobalTemplateTypeModal
            open={showEditGlobalTemplateTypeModal}
            handleClose={() => setShowEditGlobalTemplateTypeModal(false)}
          />
        </GlobalTemplatesChannelGuard>
      </OptionalComponent>
    </div>
  );
};

export default ProjectOverviewPopoverActions;
